"use client";

import { useEffect, useState } from "react";
import { ImageOff, Loader2, X } from "lucide-react";
import type { AttachmentItem } from "@/hooks/use-file-attachment-queue";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import { cn } from "@/lib/utils";

function useObjectUrl(file: File): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return url;
}

function ImageLightbox({
  src,
  alt,
  open,
  onOpenChange,
}: {
  src: string;
  alt: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "z-[60] w-auto max-w-[92vw] border-0 bg-transparent p-0 shadow-none sm:max-w-[85vw]",
          "[&>button]:right-2 [&>button]:top-2 [&>button]:rounded-full [&>button]:bg-black/60",
          "[&>button]:p-2 [&>button]:text-white [&>button]:opacity-100",
          "[&>button]:ring-offset-0 [&>button:hover]:bg-black/80"
        )}
      >
        <DialogHeader>
          <VisuallyHidden>
            <DialogTitle>{alt}</DialogTitle>
          </VisuallyHidden>
        </DialogHeader>
        <img
          src={src}
          alt={alt}
          className="max-h-[85vh] w-auto max-w-full rounded-aios-sm object-contain"
        />
      </DialogContent>
    </Dialog>
  );
}

function StagedThumbnail({
  item,
  onRemove,
}: {
  item: AttachmentItem;
  onRemove: () => void;
}) {
  const url = useObjectUrl(item.file);
  const isUploading = item.status !== "done";
  const [isOpen, setIsOpen] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <div className="relative size-14 shrink-0 overflow-hidden rounded-aios-sm border border-iai-border bg-iai-purple-4">
      {url && !failed ? (
        <>
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            aria-label={`View ${item.file.name} full size`}
            className="size-full"
          >
            <img
              src={url}
              alt={item.file.name}
              onError={() => setFailed(true)}
              className={cn(
                "size-full object-cover",
                isUploading && "opacity-50"
              )}
            />
          </button>
          <ImageLightbox
            src={url}
            alt={item.file.name}
            open={isOpen}
            onOpenChange={setIsOpen}
          />
        </>
      ) : (
        <span
          title={item.file.name}
          className="flex size-full flex-col items-center justify-center gap-0.5 px-1 text-iai-purple"
        >
          <ImageOff className="size-4 shrink-0" />
          <span className="w-full truncate text-center text-[9px] font-medium leading-none">
            {item.file.name}
          </span>
        </span>
      )}
      {isUploading ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="size-4 animate-spin text-iai-purple" />
        </span>
      ) : null}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${item.file.name}`}
        className="absolute right-1 top-1 flex size-4 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
      >
        <X className="size-2.5" strokeWidth={3} />
      </button>
    </div>
  );
}

export function AttachmentStrip({
  items,
  onRemove,
}: {
  items: AttachmentItem[];
  onRemove: (index: number) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="flex gap-2 overflow-x-auto pb-0.5">
      {items.map((item, index) => (
        <StagedThumbnail
          key={item.localId}
          item={item}
          onRemove={() => onRemove(index)}
        />
      ))}
    </div>
  );
}

function SentAttachment({
  attachment,
}: {
  attachment: { file_name: string; file_url: string };
}) {
  const [failed, setFailed] = useState(!attachment.file_url);
  const [isOpen, setIsOpen] = useState(false);

  if (failed) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-aios-sm border border-current/25 px-2 py-1.5 text-xs opacity-80">
        <ImageOff className="size-3.5" />
        {attachment.file_name}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label={`View ${attachment.file_name} full size`}
      >
        <img
          src={attachment.file_url}
          alt={attachment.file_name}
          onError={() => setFailed(true)}
          className="max-h-40 max-w-full rounded-aios-sm border border-current/25 object-contain"
        />
      </button>
      <ImageLightbox
        src={attachment.file_url}
        alt={attachment.file_name}
        open={isOpen}
        onOpenChange={setIsOpen}
      />
    </>
  );
}

export function SentAttachments({
  attachments,
}: {
  attachments: { id: string; file_name: string; file_url: string }[];
}) {
  if (attachments.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((attachment) => (
        <SentAttachment key={attachment.id} attachment={attachment} />
      ))}
    </div>
  );
}
